import { ExtractComponentProp } from '@approvalmax/types';
import { Box, Button, Flex, Popup, Radio, Text, TextField } from '@approvalmax/ui/src/components';
import { miscHelpers } from '@approvalmax/utils';
import { hooks } from '@approvalmax/utils';
import sortBy from 'lodash/sortBy';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useCurrentUser } from 'modules/utils';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePrevious, useUpdateEffect } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { AddUsersList } from 'shared/components';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { addUsers as addUsersAction } from '../../actions';
import { messages } from './AddUsersPopupContent.messages';
import { AddUsersPopupStep, InviteUsersOptions } from './AddUsersPopupContent.types';

export const AddUsersPopupContent = () => {
    const [addAsAdmin] = hooks.useQueryParam('addAsAdmin');
    const [addWithInvite] = hooks.useQueryParam('addWithInvite');

    const [messageText, setMessageText] = useState('');
    const [step, setStep] = useState<AddUsersPopupStep>(AddUsersPopupStep.AddUsers);
    const [inviteWhenAdd, setInviteWhenAdd] = useState<InviteUsersOptions>(
        addWithInvite ? InviteUsersOptions.Invite : InviteUsersOptions.NotInvite
    );
    const [userEmailsWithRoles, setUserEmailsWithRoles] = useState<Map<string, domain.CompanyUserRole>>(new Map());
    const [focusCommentTextEditor, setFocusCommentTextEditor] = useState(false);
    const prevStep = usePrevious(step);
    const prevInviteWhenAdd = usePrevious(inviteWhenAdd);
    const dispatch = useDispatch();
    const company = useCompanyBasedOnUrl();
    const me = useCurrentUser();
    const canChangeTeamMembersRoles = useSelector((state) =>
        selectors.company.canChangeTeamMembersRoles(state, company, me)
    );

    const items = useMemo(() => {
        const rows = Array.from(userEmailsWithRoles, ([email, role]) => {
            return { email, role, disabled: !canChangeTeamMembersRoles(email) };
        });

        return sortBy(rows, 'email');
    }, [canChangeTeamMembersRoles, userEmailsWithRoles]);

    useUpdateEffect(() => {
        const switchedRadio =
            prevInviteWhenAdd === InviteUsersOptions.NotInvite && inviteWhenAdd === InviteUsersOptions.Invite;
        const landedEnabled =
            prevStep !== AddUsersPopupStep.InviteUsers &&
            step === AddUsersPopupStep.InviteUsers &&
            inviteWhenAdd === InviteUsersOptions.Invite;

        if (switchedRadio || landedEnabled) {
            setFocusCommentTextEditor(true);
        }
    }, [inviteWhenAdd, prevInviteWhenAdd, prevStep, step]);

    const addUsers = useCallback(() => {
        setStep(AddUsersPopupStep.InviteUsers);
    }, []);

    const goBack = useCallback(() => {
        setStep(AddUsersPopupStep.AddUsers);
    }, []);

    const finish = useCallback(() => {
        amplitudeService.sendData('users: add user', {
            invited: inviteWhenAdd,
            'users count': userEmailsWithRoles.size,
        });

        dispatch(
            addUsersAction({
                company,
                userEmailsWithRoles: Object.fromEntries(userEmailsWithRoles.entries()),
                doInvite: inviteWhenAdd === InviteUsersOptions.Invite,
                invitationText: messageText,
            })
        );
    }, [company, dispatch, inviteWhenAdd, messageText, userEmailsWithRoles]);

    const onChangeInviteWhenAdd = useCallback<ExtractComponentProp<typeof Radio.Group, 'onChange'>>((inviteWhenAdd) => {
        if (miscHelpers.isEnumValue(InviteUsersOptions, inviteWhenAdd)) {
            setInviteWhenAdd(inviteWhenAdd);
        }
    }, []);

    const handleAddToList = useCallback(
        (validEmails: string[]) => {
            setUserEmailsWithRoles((emailsWithRoleMap) => {
                validEmails.forEach((email) => {
                    if (!emailsWithRoleMap.has(email)) {
                        const role = selectors.company.getCompanyUserRole(company, email);

                        const defaultRole =
                            addAsAdmin && canChangeTeamMembersRoles(email)
                                ? domain.CompanyUserRole.Manager
                                : domain.CompanyUserRole.Participant;

                        emailsWithRoleMap.set(email, role === domain.CompanyUserRole.None ? defaultRole : role);
                    }
                });

                return new Map(emailsWithRoleMap);
            });
        },
        [company, addAsAdmin, canChangeTeamMembersRoles]
    );

    const handleRoleChange = useCallback(
        (role: domain.CompanyUserRole, email: string) => {
            setUserEmailsWithRoles(new Map(userEmailsWithRoles.set(email, role)));
        },
        [userEmailsWithRoles]
    );

    const handleDeleteEmail = useCallback(
        (email: string) => {
            userEmailsWithRoles.delete(email);
            setUserEmailsWithRoles(new Map(userEmailsWithRoles));
        },
        [userEmailsWithRoles]
    );

    return step === AddUsersPopupStep.AddUsers ? (
        <>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button
                        color='blue80'
                        title={messages.popupButtonTitleNext}
                        disabled={userEmailsWithRoles.size === 0}
                        onClick={addUsers}
                    >
                        {messages.popupButtonTextNext}
                    </Button>
                }
            />

            <Popup.Body>
                <Text font='body' fontSize='large' spacing='0 0 8'>
                    {messages.addUsersDescription}
                </Text>

                <AddUsersList
                    items={items}
                    onAddEmails={handleAddToList}
                    onRoleChange={handleRoleChange}
                    onDeleteEmail={handleDeleteEmail}
                />
            </Popup.Body>
        </>
    ) : (
        <>
            <Popup.Header
                title={messages.title}
                actions={
                    <Flex spacing='8'>
                        <Button color='blue10' title={messages.popupButtonTitleGoBack} onClick={goBack}>
                            {messages.popupButtonTextGoBack}
                        </Button>

                        <Button color='blue80' title={messages.popupButtonTitleFinish} onClick={finish}>
                            {messages.popupButtonTextFinish}
                        </Button>
                    </Flex>
                }
            />

            <Popup.Body>
                <Flex direction='column' alignItems='start' spacing='32'>
                    <Text font='body' fontSize='large'>
                        {messages.inviteUsersDescription}
                    </Text>

                    <Flex size={24}>
                        <Radio.Group
                            name='inviteUsersRadio'
                            size='medium'
                            width='100%'
                            value={inviteWhenAdd}
                            onChange={onChangeInviteWhenAdd}
                            block
                        >
                            <Radio value={InviteUsersOptions.NotInvite}>{messages.doNotInviteRadioButtonText}</Radio>

                            <Radio value={InviteUsersOptions.Invite}>{messages.doInviteRadioButtonText}</Radio>

                            {!company.flags.isTrialLicense && (
                                <Box width='100%' spacing='0 0 0 28'>
                                    <TextField
                                        label={messages.commentTitle}
                                        multiline
                                        focus={focusCommentTextEditor}
                                        disabled={inviteWhenAdd === InviteUsersOptions.NotInvite}
                                        value={messageText}
                                        onChange={setMessageText}
                                        placeholder={messages.commentPlaceholder}
                                        size='large'
                                        minHeight={110}
                                    />
                                </Box>
                            )}
                        </Radio.Group>
                    </Flex>
                </Flex>
            </Popup.Body>
        </>
    );
};

AddUsersPopupContent.displayName = 'AddUsersPopupContent';
